"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Booking = void 0;
const jsonUtils_1 = require("benjibooks-shared/utils/jsonUtils");
const differenceInMinutes_1 = __importDefault(require("date-fns/differenceInMinutes"));
const setHours_1 = __importDefault(require("date-fns/setHours"));
const startOfToday_1 = __importDefault(require("date-fns/startOfToday"));
const dateUtils = __importStar(require("../utils/dateUtils"));
const dateUtils_1 = require("../utils/dateUtils");
const BookingStatus_1 = require("./BookingStatus");
/**
 * The booking of a resource (in Firestore db).
 */
class Booking {
    /** Creates a new Booking. */
    constructor(resourceId, config) {
        /** The start time when the resource is booked. */
        this.start = (0, setHours_1.default)((0, startOfToday_1.default)(), 13);
        /** The end time when the resource is booked. */
        this.end = (0, setHours_1.default)((0, startOfToday_1.default)(), 16);
        /** The current status of the booking. */
        this.status = BookingStatus_1.BookingStatus.booked;
        /** Booking title. */
        this.title = "";
        /** Booking subtitle. */
        this.subtitle = "";
        /** Visibility of booking title and subtitle on public displays. */
        this.publicDisplay = true;
        /** Private notes for the booking. Visible to staff only. */
        this.notes = "";
        /**
         * Lazy loaded resource config.
         *
         * `null` means it has not been set.
         * `undefined` means the Booking does not have an associated resource config.
         *
         * @see configResource
         */
        this._configResource = null;
        this._config = config;
        this.setResourceId(resourceId);
        this.tenantId = config.tenantId;
    }
    getResourceId() {
        return this.resourceId;
    }
    setResourceId(resourceId) {
        this.resourceId = resourceId;
        this.updateForResourceChange();
    }
    /**
     * Lazy loaded resource config.
     *
     * Note: The booking must be able to work without the resource config because old
     * bookings might be opened without a corresponding config.
     */
    get configResource() {
        // Lazy loading.
        if (this._configResource === null) {
            this._configResource = this._config.getResourceById(this.resourceId);
        }
        return this._configResource;
    }
    /**
     * Updates the booking to match a changed resource.
     */
    updateForResourceChange() {
        // Reset the lazy loaded resource config. Needed if resourceId changes.
        this._configResource = null;
    }
    /**
     * Creates a new booking based on the `prevBooking` with updated fields
     * from the partial `newBooking`.
     */
    static fromPartial(newBooking, prevBooking, config) {
        return Object.assign(new Booking(prevBooking.getResourceId(), config), prevBooking, newBooking);
    }
    /**
     * Constructs an object of this class from the specified JSON-compatible dictionary object.
     */
    static fromJSON(id, json, config) {
        if (typeof json.resourceId !== "string") {
            throw new Error("No valid 'resourceId' specified in JSON for Booking.");
        }
        const obj = new Booking(json.resourceId, config);
        obj.assignJSON(id, json);
        return obj;
    }
    /** Offload JSON assignment to be accessible for subclasses. */
    assignJSON(id, json) {
        this.id = id;
        Object.assign(this, json);
        // Overwrite fields that need converting.
        if (typeof json.status === "string") {
            this.status = BookingStatus_1.BookingStatus.fromName(json.status);
        }
        // Parse dates.
        if (typeof json.start === "string" || (0, dateUtils_1.isFirebaseTimestamp)(json.start)) {
            this.start = dateUtils.convertToDate(json.start);
        }
        if (typeof json.end === "string" || (0, dateUtils_1.isFirebaseTimestamp)(json.end)) {
            this.end = dateUtils.convertToDate(json.end);
        }
        if (typeof json.created === "string" || (0, dateUtils_1.isFirebaseTimestamp)(json.created)) {
            this.created = dateUtils.convertToDate(json.created);
        }
    }
    /**
     * Converts this object to JSON-compatible dictionary object.
     * All fields from `this` will be in the returned object except the `undefined` values.
     */
    toJSON() {
        const json = (0, jsonUtils_1.toJSONWithFilters)(this, {
            filterUndefined: true,
            filterUnderscore: true,
            filterEmptyString: true,
            filterEmptyArray: true,
            filterId: true,
        });
        // Overwrite fields that need converting.
        json.status = this.status.name;
        // Add resourceId as the getter is not taken.
        json.resourceId = this.resourceId;
        return json;
    }
    /**
     * Tries to find a matching item config (resource config item or addon config item)
     * for specified `type` and `title`.
     */
    findItemConfig(type, title) {
        if (this.configResource) {
            // Try to find a matching resource config.
            if (type === this.configResource.resourceItem.type &&
                title === this.configResource.resourceItem.title) {
                return this.configResource.resourceItem;
            }
            else {
                // Try to find a matching addon config.
                return this.configResource.addonItems.find((addon) => addon.type === type && addon.title === title);
            }
        }
        return undefined;
    }
    /** Returns the duration in hours (with fractions). */
    durationInHours() {
        return (0, differenceInMinutes_1.default)(this.end, this.start) / 60;
    }
    /** Returns the duration as a formatted string of how many hours (e.g. "2.5 Stunden"). */
    durationFormatted() {
        if (this.end && this.start) {
            const duration = this.durationInHours();
            if (duration === 1) {
                return `1 Stunde`;
            }
            else if (duration >= 0) {
                return `${duration} Stunden`;
            }
        }
        return undefined;
    }
}
exports.Booking = Booking;
Booking.titleMaxLength = 40;
Booking.subtitleMaxLength = 40;
